/* KANON `Programy v2 - D3 (0.8)` — the inspector's cover plate, transcribed.
 *
 * Read off the rendered specimen at 1512x1000:
 *   plate    976 x 217.5, full-bleed inside the card, the photograph under a three-stop veil
 *   top      inset 16 / 19 / 19, height 35, the chip left and `Edytuj` right
 *   chip     21 tall, a 5px category dot at radius 2 beside the word (owner, 4 Nov 2026: no plate)
 *   Edytuj   35 tall, 0 17.5 padding, radius 10.5, white fill with --chip-solid-ink
 *   foot     inset 19 / 19 / 16 from the bottom, a 5px column
 *   title    27 / 800 / --ls-empty on --lh-display, the ONE leaf here that is not `normal`
 *   KPI      8 of top padding, 24 between figures, figure 17 / 800 on --lh-none, word 12 / 500
 *            then the mode's mark, 18, alone at the row's far right
 *
 * The owner's polish of 4 November 2026 took out the canon's sub-line and its reserved line, the
 * chip's translucent plate and the KPI word's tracked capitals; the README's register has each.
 *
 * ── The ink is theme-INDEPENDENT, and that is the port rather than an omission ───────────────
 * A photograph under a dark veil is a photograph in both themes. `var(--text)` would be black on
 * Flame and disappear; every colour below is one of the `--…-on-cover` / `--…-on-photo` constants,
 * declared once in `colors.css` and never overridden per theme.
 *
 * **Every text leaf states its line-height.** `base.css` puts `--lh-relaxed` on `body` and the
 * specimen's body carries none, so an unstated leaf renders about 2px taller than the canon.
 *
 * Heights and widths are layout rather than design tokens (see stylelint.config.js); every colour,
 * space, radius and type value below is a token. */

.hero {
  position: relative;
  flex: none;
  height: var(--h-pl-hero);
  overflow: hidden;
}

/* The fit and the neutral ground are `shared/ui/photoBox`'s `.photo`, applied as a second class in
 * the TSX; the geometry is this file's. The plate is not lazy: it is the one image on the pane and
 * the queue behind the list has already warmed it. */
.cover {
  display: block;
  width: 100%;
  height: 100%;
}

.veil {
  position: absolute;
  inset: 0;
  background: var(--scrim-pl-hero);
}

/* ── The chip and the one control ───────────────────────────────────────────────────────────── */

.top {
  position: absolute;
  top: var(--space-4);
  right: var(--space-19px);
  left: var(--space-19px);
  display: flex;
  gap: var(--space-2);
  align-items: center;
  height: var(--h-pl-action);
}

.chip {
  display: inline-flex;
  flex: none;
  gap: var(--space-5-5px);
  align-items: center;
  height: var(--h-pl-chip);
  font-size: var(--type-label);
  font-weight: var(--fw-semibold);
  line-height: var(--lh-auto);
  color: var(--text-on-photo);
  white-space: nowrap;
}

/* `--cat-ink` is set on `.hero` by `shared/ui/planCategoryColour`: the raw hue in Flame Dark and
 * the canon's own darker step in Flame. Deviation D10 records why this app uses its one category
 * ink where the specimen paints the raw hue on both themes. */
.chipDot {
  flex: none;
  width: 5px;
  height: 5px;
  background: var(--cat-ink);
  border-radius: var(--radius-hair);
}

/* The specimen's own `flex: 1` spacer. It is what pins `Edytuj` to the plate's right edge, so
 * dropping the `Duplikuj` ghost beside it (owner decision D2) moves nothing. */
.spacer {
  flex: 1;
}

/* No coloured glow: the shadow is neutral ink, exactly as the specimen writes it. */
.edit {
  display: inline-flex;
  flex: none;
  align-items: center;
  height: var(--h-pl-action);
  padding: var(--space-0) var(--space-17-5px);
  font-size: var(--fs-label);
  font-weight: var(--fw-bold);
  line-height: var(--lh-auto);
  letter-spacing: var(--ls-heading);
  color: var(--chip-solid-ink);
  text-decoration: none;
  white-space: nowrap;
  background: var(--chip-solid-fill);
  border-radius: var(--radius-10-5px);
  box-shadow: var(--shadow-pl-edit);
}

.edit:focus-visible {
  outline: var(--border-focus) solid var(--chip-solid-fill);
  outline-offset: var(--outline-inset);
}

/* ── What the plan is ───────────────────────────────────────────────────────────────────────── */

.foot {
  position: absolute;
  right: var(--space-19px);
  bottom: var(--space-4);
  left: var(--space-19px);
  display: flex;
  flex-direction: column;
  gap: var(--space-5px);
}

/* The one leaf on this plate whose line-height is not `normal`. It was the specimen's 1.05
 * (`--lh-heading`), and with the ellipsis's `overflow: hidden` that clipped every descender — the
 * tail of a `y`, a `g`, an ogonek. 1.2 keeps them, and the pixel under the line is the rest of the
 * glyph box the clip would otherwise take (owner, 4 November 2026). */
.title {
  margin: var(--space-0);
  padding-block-end: var(--space-1px);
  overflow: hidden;
  font-size: var(--fs-pl-hero-title);
  font-weight: var(--fw-extrabold);
  line-height: var(--lh-display);
  letter-spacing: var(--ls-empty);
  color: var(--text-on-photo);
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* ── The two figures and the mark ──────────────────────────────────────────────────────────── */

.kpis {
  display: flex;
  gap: var(--space-6);
  align-items: flex-end;
  padding: var(--space-2) var(--space-0) var(--space-0);
}

.kpi {
  display: flex;
  flex-direction: column;
  gap: var(--space-2-5px);
}

.figure {
  font-size: var(--fs-pl-kpi);
  font-weight: var(--fw-extrabold);
  line-height: var(--lh-none);
  letter-spacing: var(--ls-display);
  color: var(--text-on-photo);
  font-variant-numeric: tabular-nums;
}

.label {
  font-size: var(--type-label);
  font-weight: var(--fw-medium);
  line-height: var(--lh-auto);
  color: var(--text-3-on-cover);
  white-space: nowrap;
}

/* The mode's symbol, alone and pushed to the plate's right edge, level with the figures rather than
 * with the words under them. Its name is `PlanModeMark`'s hidden text and its tooltip. */
.mode {
  display: flex;
  flex: none;
  align-self: flex-start;
  margin-inline-start: auto;
  color: var(--text-on-photo);
}

.mode svg {
  display: block;
  width: 18px;
  height: 18px;
}
